import { useEffect, useRef, useState } from 'react'
import { PressScale } from '../../lib/PressScale'

/**
 * InviteComposer — the one high-intent control on the team screen.
 *
 * A real `<form onSubmit>`, so **Enter submits** (the wizard's convention).
 * `type="email"` + `inputMode="email"` + `autoComplete="email"`, and the input
 * is 16px because anything smaller makes iOS zoom the page on focus.
 *
 * `.lqd-pill` is one of exactly two places glass is allowed on this surface:
 * it is a form field, which is what that recipe is for. (The other is the
 * paywall panel — chrome. A 12-row list of `.lqd-glass` is 12 stacked
 * backdrop-filters and janks on a low-end Android.)
 *
 * ── THE EMAIL SURVIVES EVERYTHING ─────────────────────────────────────────
 * Hitting the seat limit, cancelling Stripe, a failed send — none of them
 * clear the field. `value` is owned by the panel for exactly that reason: the
 * difference between a paywall and an upgrade is whether the person has to
 * retype the name of the colleague they were trying to add.
 */
export function InviteComposer({
  value,
  onChange,
  role,
  onRoleChange,
  canInviteAdmin,
  busy,
  error,
  onSubmit,
  autoFocus = false,
}: {
  value: string
  onChange: (v: string) => void
  role: 'admin' | 'member'
  onRoleChange: (r: 'admin' | 'member') => void
  /** Only the OWNER may mint an admin — an org admin doing it is the same
   *  privilege grant `mayChangeMemberRole` reserves, through another door, and
   *  the server 403s it. Don't offer what will be refused. */
  canInviteAdmin: boolean
  busy: boolean
  /** Rendered under the field, in place. Never a toast. */
  error?: string | null
  onSubmit: () => void
  autoFocus?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [touched, setTouched] = useState(false)
  useEffect(() => { if (autoFocus) inputRef.current?.focus() }, [autoFocus])

  const trimmed = value.trim()
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
  const showFormatHint = touched && trimmed.length > 0 && !looksLikeEmail

  return (
    <form
      className="mt-5"
      onSubmit={(e) => { e.preventDefault(); setTouched(true); if (looksLikeEmail && !busy) onSubmit() }}
      data-testid="team-invite-form"
    >
      <label htmlFor="team-invite-email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">
        Invite a teammate
      </label>
      <div className="lqd-pill flex min-h-[52px] flex-wrap items-center gap-2 rounded-full px-3 py-1.5 sm:flex-nowrap">
        <input
          ref={inputRef}
          id="team-invite-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          spellCheck={false}
          placeholder="name@company.com"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          className="min-w-0 flex-1 border-0 bg-transparent px-2 text-[16px] text-ink placeholder:text-navy-300 focus:outline-none"
          data-testid="team-invite-email"
        />
        {canInviteAdmin && (
          <>
            <label htmlFor="team-invite-role" className="sr-only">Role for the person you’re inviting</label>
            <select
              id="team-invite-role"
              value={role}
              onChange={(e) => onRoleChange(e.target.value === 'admin' ? 'admin' : 'member')}
              className="h-11 shrink-0 rounded-full border-0 bg-transparent px-2 text-[13px] font-medium text-soft focus:outline-none focus-visible:shadow-[0_0_0_3px_var(--gold-300)]"
              data-testid="team-invite-role"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </>
        )}
        <PressScale
          feel="tap"
          type="submit"
          disabled={busy}
          className="btn-primary h-11 shrink-0 px-5"
          data-testid="team-invite-submit"
        >
          {busy ? 'Sending…' : 'Invite'}
        </PressScale>
      </div>
      {showFormatHint && (
        <p className="mt-2 px-1 text-[12.5px] text-soft" data-testid="team-invite-hint">
          That doesn’t look like an email address yet.
        </p>
      )}
      {error && (
        <p className="mt-2 px-1 text-[13px] font-medium text-danger" role="alert" data-testid="team-invite-error">
          {error}
        </p>
      )}
    </form>
  )
}
